import {
  Box, Flex, IconButton, Image, Stack, Divider, Text, Tooltip, useColorMode, Collapse,
} from "@chakra-ui/react";
import { useLocation, Link } from "react-router";
import { Fragment, PropsWithChildren, useEffect } from "react";
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarLeftExpand } from "react-icons/tb";
import { findMostSpecificSidebarItem, sidebarItemsFor } from "definitions/sidebarDefinitions";
import { baseMyOnboarding } from "definitions/sidebarDefinitions/shared";
import ROLE from "models/role";
import Logo from "assets/Logo.svg";
import DarkModeLogo from "assets/CaminoDarkModeLogo.svg";
import useCaminoStore from "hooks/useCaminoStore";
import { ADMIN_ROUTES, SHARED_ROUTES } from "definitions/constants/routeConstants";
import { MdAdminPanelSettings } from "react-icons/md";
import { usePendingNewHireJourneysCountQuery } from "features/PendingNewHire/hooks";
import { useIssueCountQuery } from "features/Issues/hooks";
import useSidebarState from "hooks/useSidebarState";
import { useTasksCountQuery } from "features/Resources/Task/hooks";
import AddNewHireOrExistingEmployeeButton from "features/NewHireJourney/NewHireForm/AddNewHireOrExistingEmployeeButton";
import AvatarDropdown from "components/AvatarDropdown";
import FeatureGate from "components/FeatureGate";
import { useFeatureContext } from "contexts/FeatureContext";
import SidebarNavButton, { BadgeProps } from "./SidebarNavButton";
import SidebarNavButtonWithPopover from "./SidebarNavButtonWithPopover";
import MakeAWishButton from "./MakeAWishButton";
import ReturnToMainButton from "./ReturnToMainButton";

interface SidebarHeaderSectionProps extends PropsWithChildren {
  header?: string;
  expanded?: boolean;
}

function SidebarSection({ children, expanded = true, header = undefined }: SidebarHeaderSectionProps) {
  return (
    <Stack>
      <Collapse in={expanded} transition={{ enter: { duration: ".6" }, exit: { duration: ".6" } }}>
        {header && (
          <Text textStyle="sm" color="fg.subtle" fontWeight="medium">
            {header}
          </Text>
        )}
      </Collapse>
      <Stack spacing="1">{children}</Stack>
    </Stack>
  );
}

function sidebarRoleFor(pathname: string, permissions: ROLE[]): ROLE | null {
  if (pathname.startsWith("/my-onboarding")) return ROLE.ONBOARDER;
  if (permissions.includes(ROLE.COMPANY_ADMIN) || permissions.includes(ROLE.PEOPLE_TEAM)) return ROLE.PEOPLE_TEAM;
  if (permissions.includes(ROLE.MANAGER)) return ROLE.MANAGER;
  if (permissions.includes(ROLE.IT_ADMIN)) return ROLE.IT_ADMIN;
  if (permissions.includes(ROLE.BASIC) || permissions.includes(ROLE.ONBOARDER)) return ROLE.BASIC;
  return null;
}

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const SHORTCUT_LABEL = isMac ? "⌘." : "Ctrl+.";

export default function Sidebar() {
  const location = useLocation();
  const user = useCaminoStore((state) => state.currentUser);
  const currentCompany = useCaminoStore((state) => state.currentCompany);
  const { isExpanded, toggleSidebar } = useSidebarState();
  const { colorMode } = useColorMode();
  const { isEnabled } = useFeatureContext();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === ".") {
        event.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  const isNewHirePortal = location.pathname.startsWith("/my-onboarding");
  const computedRole = sidebarRoleFor(location.pathname, user?.permission || []);

  const { data: issuesCount } = useIssueCountQuery(undefined, computedRole === ROLE.PEOPLE_TEAM);
  const { data: pendingNewHiresCount } = usePendingNewHireJourneysCountQuery({
    enabled: computedRole === ROLE.PEOPLE_TEAM,
  });
  const { data: tasksCount } = useTasksCountQuery(isNewHirePortal ? user?.newHireJourneyId : undefined);

  if (!computedRole || !user) {
    return (
      <Box p="4" color="fg.subtle" fontSize="sm">
        No sidebar — user is a camino team member with no roles.
      </Box>
    );
  }

  // Keyed by sidebar item id so renaming the label doesn't silently drop the badge.
  const notificationCounts: { [key: string]: number | undefined } = {
    issues: issuesCount?.count,
    "new-hire-journeys": pendingNewHiresCount?.count,
    tasks: tasksCount?.count,
  };

  const rawSidebarItems = sidebarItemsFor(location.pathname, user.permission || []);
  const sidebarItems = rawSidebarItems.map((section, index) => {
    let items = (section.items || []).filter((item) => !item.featureFlag || isEnabled(item.featureFlag));
    // Inject "My Onboarding" after "Home" in the first section when the user has an active journey
    if (index === 0 && !isNewHirePortal && user?.newHireJourneyId) {
      const homeIndex = items.findIndex((item) => item.id === "home");
      if (homeIndex >= 0) {
        items = [
          ...items.slice(0, homeIndex + 1),
          baseMyOnboarding,
          ...items.slice(homeIndex + 1),
        ];
      }
    }
    return { ...section, items };
  });
  const activeItemId = findMostSpecificSidebarItem(location.pathname, sidebarItems);

  return (
    <Flex
      as="section"
      maxH="100vh"
      minH="100vh"
      minW={isExpanded ? "220px" : "80px"}
      bg="bg.primary"
      p="2"
    >
      <Flex
        flex="1"
        bg="bg.secondary"
        borderRadius="md"
        boxShadow="md"
        overflowY="scroll"
        p="4"
        maxW={{ base: "full", sm: "xs" }}
        minHeight="100%"
      >
        <Stack
          justify="space-between"
          spacing="1"
          width="full"
          minHeight="100%"
        >
          <Stack
            spacing="0"
            shouldWrapChildren
          >
            <Flex
              justifyContent={isExpanded ? "space-between" : "center"}
              alignItems="center"
              pl={isExpanded ? "2" : 0}
              mb="2"
              minH="40px"
            >
              {isExpanded && (
                <Link to={SHARED_ROUTES.home}>
                  <Image
                    maxWidth="140px"
                    maxHeight="20px"
                    alt="Logo"
                    src={colorMode === "dark" ? DarkModeLogo : Logo}
                  />
                </Link>
              )}
              <Tooltip
                label={`${isExpanded ? "Close" : "Open"} sidebar  ${SHORTCUT_LABEL}`}
                placement={isExpanded ? "bottom" : "right"}
                hasArrow
                openDelay={300}
              >
                <IconButton
                  aria-label={`${isExpanded ? "Close" : "Open"} sidebar`}
                  icon={
                    isExpanded
                      ? <TbLayoutSidebarLeftCollapse strokeWidth={1.5} size={18} />
                      : <TbLayoutSidebarLeftExpand strokeWidth={1.5} size={18} />
                  }
                  onClick={toggleSidebar}
                  size="sm"
                  variant="ghost"
                  color="fg.subtle"
                />
              </Tooltip>
            </Flex>
            <Box mt="4">
              <Stack spacing="2">
                {isNewHirePortal && <ReturnToMainButton isExpanded={isExpanded} />}
                {computedRole === ROLE.PEOPLE_TEAM && (
                  <Flex justify="center">
                    <AddNewHireOrExistingEmployeeButton isExpanded={isExpanded} />
                  </Flex>
                )}
              </Stack>
              {sidebarItems.map(({ header, items, id }) => (
                <Fragment key={`fragment-${id}`}>
                  <Divider key={`divider-${id}`} my="12px" />
                  <SidebarSection expanded={isExpanded} key={`section-${id}`} header={header}>
                    {null}
                    {(items || []).map(({
                      id: itemId, name, route, icon, subItems,
                    }) => {
                      const notificationCount = notificationCounts[itemId] || 0;
                      let badge: BadgeProps | undefined = undefined;
                      if (notificationCount > 0) {
                        if (itemId === "new-hire-journeys" || itemId === "tasks") {
                          badge = { value: notificationCount, color: computedRole === ROLE.MANAGER ? undefined : notificationCount > 5 ? "red" : "green" };
                        } else if (itemId === "issues") {
                          badge = { value: true, color: "red" };
                        }
                      }

                      if (subItems && subItems.length > 0) {
                        return (
                          <SidebarNavButtonWithPopover
                            key={`navbutton-${id}-${name}`}
                            route={route}
                            label={name}
                            icon={icon}
                            expanded={isExpanded}
                            isActive={activeItemId === itemId}
                            badge={badge}
                            subItems={subItems}
                          />
                        );
                      }

                      return (
                        <SidebarNavButton
                          route={route}
                          key={`navbutton-${id}-${name}`}
                          label={name}
                          icon={icon}
                          expanded={isExpanded}
                          isActive={activeItemId === itemId}
                          badge={badge}
                        />
                      );
                    })}
                  </SidebarSection>
                </Fragment>
              ))}
            </Box>
          </Stack>
          <Stack gap="1">
            {currentCompany?.slug === "camino" && (
              <FeatureGate feature="admin_panel">
                <SidebarNavButton
                  subtle
                  route={ADMIN_ROUTES.hub}
                  icon={MdAdminPanelSettings}
                  label="Admin"
                  expanded={isExpanded}
                  isActive={location.pathname.startsWith(ADMIN_ROUTES.hub)}
                />
              </FeatureGate>
            )}
            <MakeAWishButton expanded={isExpanded} />
            <AvatarDropdown isExpanded={isExpanded} />
          </Stack>
        </Stack>
      </Flex>
    </Flex>
  );
}
